/**
 * PDF export — a print-styled rendering of the pour record.
 *
 * Spec, "Export":
 *   "PDF — a print-styled rendering of the pour record that mirrors the
 *    existing printed layout, so what a client receives is recognisably the
 *    same document they have today."
 *
 * Implementation choice: a self-contained HTML document with an embedded print
 * stylesheet, handed to the browser's own `window.print()` (Save as PDF). No
 * PDF library is bundled. That matters on site WiFi — a client-side PDF engine
 * is hundreds of kilobytes of JavaScript for a document the browser already
 * knows how to make — and it gives real text layout: repeating table headers,
 * proper page breaks, selectable text and searchable notes.
 *
 * Everything except `printHtmlDocument` / `printPourRecord` is PURE: given the
 * record, it returns the document as a string. That is what makes the layout
 * unit-testable without a DOM, and it means the same function could be served
 * from the Worker if a server-rendered print view is ever wanted.
 *
 * The block order deliberately matches `./csv.ts` so the two documents can
 * never tell different stories about the same pour.
 */

import {
  CHUTE_TEST_POINT_LABELS,
  type CalibrationCheck,
  type CalibrationTrial,
  type ChuteTest,
  type FlowVerdict,
  type ItpItem,
  type PourFull,
  type PrePourCalibration,
  type SpreadVerdict,
  type SqlBool,
  type StopHoldEvent
} from '../types';
import {
  calibrationCheckComputed,
  flowVerdict,
  photoAddedAfterClose,
  roundTo,
  spreadVerdict,
  waterCheck,
  wcRatioDisplay,
  wtPerCount
} from '../calc';
import {
  SCI_LOGO_ALT,
  SCI_LOGO_DATA_URI,
  SCI_LOGO_HEIGHT,
  SCI_LOGO_WIDTH
} from '../brand';
import { apiPath } from '../api-contract';
import type { ExportProject } from './csv';

/* ==========================================================================
 * Escaping and small formatters
 * ======================================================================== */

/**
 * HTML text escaping.
 *
 * Notes are free text typed on site and routinely contain `<`, `&` and quotes.
 * Every value that reaches the document goes through here; there is no path
 * that interpolates a record value unescaped.
 */
export function escapeHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Empty values print as an em dash so a blank line is visibly blank, not lost. */
const EMPTY = '&mdash;';

function text(value: unknown): string {
  if (value === null || value === undefined || value === '') return EMPTY;
  return escapeHtml(value);
}

function yesNo(value: SqlBool | null | undefined): string {
  return value === 1 ? 'Yes' : value === 0 ? 'No' : EMPTY;
}

/**
 * The company mark as a letterhead image.
 *
 * Shared by all three printed documents — the pour record below, the mix
 * design sheet and the quality sheets — so the letterhead can never differ
 * between them.
 *
 * Sized in millimetres, and in the markup rather than the stylesheet, for two
 * reasons: a print document has a real physical width, so a mark specified in
 * pixels is at the mercy of whatever DPI the printer assumes; and each of the
 * three documents carries its own stylesheet, so keeping the size here is what
 * keeps one call from printing a mark twice the size of another's.
 */
export function sciLogoHtml(widthMm: number): string {
  const heightMm = roundTo((widthMm * SCI_LOGO_HEIGHT) / SCI_LOGO_WIDTH, 2);
  return (
    `<img class="sci-mark" src="${SCI_LOGO_DATA_URI}" alt="${escapeHtml(SCI_LOGO_ALT)}"` +
    ` style="display:block;width:${widthMm}mm;height:${heightMm}mm">`
  );
}

/**
 * Short verdict wording. The full labels in ../types spell out the corrective
 * action ("Below 16 in - add water"), which is right for a phone screen and too
 * long for a printed table column. Exported so the Worker's fallback PDF prints
 * the same words.
 */
export const SPREAD_VERDICT_SHORT: Record<SpreadVerdict, string> = {
  in_range: 'In range',
  add_water: 'Add water',
  reduce_water: 'Reduce water'
};

export const FLOW_VERDICT_SHORT: Record<FlowVerdict, string> = {
  in_range: 'In range',
  too_much_water: 'Too much water',
  not_enough_water: 'Not enough water'
};

/* ==========================================================================
 * The print stylesheet
 * ======================================================================== */

/**
 * Mirrors the printed checklist: US Letter portrait, ruled boxes for the fixed
 * sections, bordered tables for the repeating logs. A Hold-point row in the
 * ITP table is NOT shaded or bolded — the Type column ("H", "H/W", "H/R") is
 * the only marker for it. Only an unresolved Hold point blocking a close gets
 * visual weight, and that is the `.alarm` banner at the top of the document,
 * not row styling.
 *
 * Print-specific rules that do the real work:
 *   `thead { display: table-header-group }` repeats column headers on every
 *   page; `tr { break-inside: avoid }` keeps a chute test on one page; and
 *   `print-color-adjust: exact` keeps the `.alarm` banner's colour when a
 *   browser would otherwise drop backgrounds.
 */
export const POUR_PRINT_STYLESHEET = `
*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; padding: 0; }
body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
  font-size: 9pt;
  line-height: 1.35;
  color: #10161d;
  background: #fff;
  padding: 10mm;
}
h1 { font-size: 15pt; margin: 0 0 1mm; letter-spacing: 0.02em; }
h2 {
  font-size: 10pt;
  margin: 6mm 0 1.5mm;
  padding-bottom: 0.8mm;
  border-bottom: 1pt solid #10161d;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
p { margin: 0 0 1mm; }
.doc { max-width: 190mm; margin: 0 auto; }
.letterhead { padding-top: 2mm; margin-bottom: 3mm; text-align: center; }
.letterhead img { margin-inline: auto; }
.dochead { border: 1.2pt solid #10161d; padding: 3mm; margin-bottom: 3mm; }
.dochead .sub { font-size: 8.5pt; color: #46525f; }
.docmeta {
  display: flex;
  flex-wrap: wrap;
  gap: 1mm 6mm;
  margin-top: 2mm;
  padding-top: 2mm;
  border-top: 0.5pt solid #aab5c0;
  font-size: 8.5pt;
}
.docmeta b { font-weight: 700; }
.alarm {
  border: 1.5pt solid #a81b0c;
  background: #fce7e4;
  color: #a81b0c;
  padding: 2.5mm 3mm;
  margin: 0 0 3mm;
  font-weight: 700;
  font-size: 9pt;
  print-color-adjust: exact;
  -webkit-print-color-adjust: exact;
}
table { width: 100%; border-collapse: collapse; }
.fields td { border: 0.5pt solid #aab5c0; padding: 1.2mm 1.8mm; vertical-align: top; }
.fields td.k { width: 26%; background: #f1f4f7; font-weight: 600; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
.log { font-size: 7.5pt; }
.log th, .log td { border: 0.5pt solid #aab5c0; padding: 1mm 1.2mm; text-align: left; vertical-align: top; }
.log th { background: #e6eef6; font-weight: 700; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
.log td.num, .log th.num { text-align: right; font-variant-numeric: tabular-nums; }
.log td.notes { font-size: 7pt; color: #46525f; }
.empty { padding: 2mm; border: 0.5pt dashed #aab5c0; color: #6b7784; font-size: 8pt; }
.plates {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3mm;
  margin-top: 2mm;
}
.plate { margin: 0; break-inside: avoid; page-break-inside: avoid; }
.plate a { display: block; text-decoration: none; color: inherit; }
.plate img {
  display: block;
  width: 100%;
  height: auto;
  max-height: 70mm;
  object-fit: contain;
  border: 0.5pt solid #aab5c0;
  background: #fff;
  print-color-adjust: exact;
  -webkit-print-color-adjust: exact;
}
.plate figcaption { margin-top: 1mm; font-size: 7.5pt; }
.plate .who { display: block; color: #46525f; font-size: 7pt; }
.plate .late { display: block; color: #a81b0c; font-weight: 700; font-size: 7.5pt; }
.foot {
  margin-top: 8mm;
  padding-top: 2mm;
  border-top: 0.5pt solid #aab5c0;
  font-size: 7.5pt;
  color: #46525f;
}
@page { size: Letter portrait; margin: 12mm; }
@media print {
  body { padding: 0; font-size: 8.5pt; }
  .doc { max-width: none; }
  h2 { break-after: avoid; page-break-after: avoid; }
  table { break-inside: auto; }
  thead { display: table-header-group; }
  tr { break-inside: avoid; page-break-inside: avoid; }
  .alarm, .dochead { break-inside: avoid; page-break-inside: avoid; }
  .noprint { display: none !important; }
}
`.trim();

/* ==========================================================================
 * Building blocks
 * ======================================================================== */

type Field = readonly [label: string, value: unknown];

function fieldTable(fields: readonly Field[]): string {
  const rows = fields
    .map(([label, value]) => `<tr><td class="k">${escapeHtml(label)}</td><td>${text(value)}</td></tr>`)
    .join('');
  return `<table class="fields"><tbody>${rows}</tbody></table>`;
}

interface LogColumn {
  label: string;
  /** Right-align and tabular-figure the column. */
  num?: boolean;
}

interface LogCell {
  value: unknown;
  num?: boolean;
}

interface LogRow {
  cells: readonly LogCell[];
  /** Free-text note printed as a full-width row directly beneath. */
  note?: string | null;
}

function logTable(columns: readonly LogColumn[], rows: readonly LogRow[], empty: string): string {
  if (rows.length === 0) return `<p class="empty">${escapeHtml(empty)}</p>`;

  const head = columns
    .map((column) => `<th${column.num ? ' class="num"' : ''}>${escapeHtml(column.label)}</th>`)
    .join('');

  const body = rows
    .map((row) => {
      const cells = row.cells
        .map((cell) => `<td${cell.num ? ' class="num"' : ''}>${text(cell.value)}</td>`)
        .join('');
      const main = `<tr>${cells}</tr>`;
      if (!row.note) return main;
      return `${main}<tr><td class="notes" colspan="${columns.length}">${escapeHtml(row.note)}</td></tr>`;
    })
    .join('');

  return `<table class="log"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

/* ==========================================================================
 * Sections
 * ======================================================================== */

/** Trials are records only — no verdict column, because there is no verdict. */
function calibrationTrialsTable(trials: readonly CalibrationTrial[]): string {
  const columns: LogColumn[] = [
    { label: 'Trial', num: true },
    { label: 'Reading (kg)', num: true },
    { label: 'Counts', num: true },
    { label: 'Wt/count', num: true }
  ];

  const rows: LogRow[] = trials.map((trial) => ({
    cells: [
      { value: trial.trial_no, num: true },
      { value: trial.reading_kg, num: true },
      { value: trial.counts, num: true },
      { value: roundTo(wtPerCount(trial.reading_kg, trial.counts), 4), num: true }
    ]
  }));

  return logTable(columns, rows, 'No calibration trials recorded.');
}

/**
 * The two verification checks.
 *
 * The tolerance column reads "Site +/-2%", never CSA A23.1. CSA is +/-1%; this
 * band is a deliberate site allowance, and a printed record that implied
 * otherwise would misstate what was actually applied.
 */
function calibrationChecksTable(checks: readonly CalibrationCheck[]): string {
  const columns: LogColumn[] = [
    { label: 'Check', num: true },
    { label: 'Check counts', num: true },
    { label: 'Check (kg)', num: true },
    { label: 'Check wt/count', num: true },
    { label: 'Actual counts', num: true },
    { label: 'Actual (kg)', num: true },
    { label: 'Actual wt/count', num: true },
    { label: 'Variance', num: true },
    { label: 'Site +/-2%' }
  ];

  const rows: LogRow[] = checks.map((check) => {
    const computed = calibrationCheckComputed(check);
    const variance = computed.variance;
    return {
      cells: [
        { value: check.check_no, num: true },
        { value: check.check_counts, num: true },
        { value: check.check_kg, num: true },
        { value: check.check_wt_per_count, num: true },
        { value: check.actual_counts, num: true },
        { value: check.actual_kg, num: true },
        { value: computed.actual_wt_per_count, num: true },
        {
          value: variance
            ? `${variance.deviation >= 0 ? '+' : '-'}${variance.deviation_percent.toFixed(2)}%`
            : null,
          num: true
        },
        { value: variance ? (variance.verdict === 'pass' ? 'PASS' : 'FAIL') : null }
      ]
    };
  });

  return logTable(columns, rows, 'No calibration checks recorded.');
}

function chuteTestsTable(tests: readonly ChuteTest[]): string {
  const columns: LogColumn[] = [
    { label: '#', num: true },
    { label: 'Test point' },
    { label: 'Time' },
    { label: 'Vol m3', num: true },
    { label: 'Spread in', num: true },
    { label: 'Spread verdict' },
    { label: 'Flow s', num: true },
    { label: 'Flow verdict' },
    { label: 'Mud lb/ft3', num: true },
    { label: 'Grout C', num: true },
    { label: 'Counts', num: true },
    { label: 'Grout kg', num: true },
    { label: 'Water L', num: true },
    { label: 'W/C', num: true },
    { label: 'Acc' },
    { label: 'By' }
  ];

  const rows: LogRow[] = tests.map((test) => {
    const spread = spreadVerdict(test.spread_in);
    const flow = flowVerdict(test.flow_seconds);
    return {
      note: test.notes,
      cells: [
        { value: test.seq, num: true },
        { value: CHUTE_TEST_POINT_LABELS[test.test_point] ?? test.test_point },
        { value: test.sample_time },
        { value: test.approx_volume_m3, num: true },
        { value: test.spread_in, num: true },
        { value: spread ? SPREAD_VERDICT_SHORT[spread] : null },
        { value: test.flow_seconds, num: true },
        { value: flow ? FLOW_VERDICT_SHORT[flow] : null },
        // Mud balance: capture only, no verdict column — no acceptance range
        // has been supplied and a QA record must not invent one.
        { value: test.mud_balance, num: true },
        { value: test.grout_temp_c, num: true },
        { value: test.counts, num: true },
        { value: test.grout_wt_kg, num: true },
        { value: test.water_vol_l, num: true },
        // Printed the way the existing workbook prints it: truncated to 2 dp.
        { value: wcRatioDisplay(test.water_vol_l, test.grout_wt_kg), num: true },
        { value: yesNo(test.accepted) },
        { value: test.tester_initials }
      ]
    };
  });

  return logTable(columns, rows, 'No chute tests recorded.');
}

function stopHoldTable(events: readonly StopHoldEvent[]): string {
  const columns: LogColumn[] = [
    { label: '#', num: true },
    { label: 'Time stopped' },
    { label: 'Reason' },
    { label: 'Corrective action' },
    { label: 'Extra test' },
    { label: 'Restart' },
    { label: 'QC acceptance' }
  ];

  const rows: LogRow[] = events.map((event) => ({
    cells: [
      { value: event.event_no, num: true },
      { value: event.time_stopped },
      { value: event.reason },
      { value: event.corrective_action },
      { value: yesNo(event.additional_test_required) },
      { value: event.restart_time },
      { value: event.qc_acceptance_notes }
    ]
  }));

  return logTable(columns, rows, 'No stop-hold events recorded.');
}

function calibrationWorksheetFields(cal: PrePourCalibration): Field[] {
  const water = waterCheck(cal.trial_water_litres, cal.trial_water_kg);
  return [
    ['Wt/count used (kg/count)', cal.trial_wt_per_count_used],
    ['Water check', `${cal.trial_water_litres ?? '-'} L / ${cal.trial_water_kg ?? '-'} kg`],
    [
      'Water check +/-1%',
      water
        ? `${roundTo(water.ratio, 4)} (${water.deviation >= 0 ? '+' : '-'}${water.deviation_percent.toFixed(2)}%) ${water.verdict === 'pass' ? 'PASS' : 'FAIL'}`
        : null
    ],
    ['Test start / end', `${cal.trial_test_start ?? '-'} / ${cal.trial_test_end ?? '-'}`],
    ['Material temp (C)', cal.trial_temp_material],
    ['Ambient temp (C)', cal.trial_temp_ambient]
  ];
}

/* ==========================================================================
 * The document
 * ======================================================================== */

export interface PourPrintInput {
  project: ExportProject;
  pour: PourFull;
  /** Stamped in the document head. Defaults to now. */
  exported_at?: string;
  /** Shift name of whoever produced the document, when known. */
  exported_by?: string | null;
  /**
   * Failed Hold points that would refuse a close. Printed as a banner at the
   * top: an exported record with an unresolved Hold point must say so on its
   * face, not only in the ITP table.
   */
  blocking_hold_points?: readonly ItpItem[];
}

/** Just the `<body>` inner HTML — usable for an in-page preview. */
export function buildPourPrintBody(input: PourPrintInput): string {
  const { project, pour: full } = input;
  const cal = full.calibration;
  const exportedAt = input.exported_at ?? new Date().toISOString();
  const blocking = input.blocking_hold_points ?? [];

  const parts: string[] = ['<div class="doc">'];

  parts.push(
    // The mark sits above the bordered head rather than inside it, centred:
    // the box is the record's identity block, and a letterhead belongs on the
    // page, not in the box.
    `<div class="letterhead">${sciLogoHtml(46)}</div>`,
    '<div class="dochead">',
    '<h1>SCI Field QC &mdash; Pour record</h1>',
    `<p class="sub">${text(project.name)} (${text(project.code)}) &middot; Client: ${text(project.client)}</p>`,
    '<div class="docmeta">',
    `<span><b>Pour</b> ${text(full.pour.label)}</span>`,
    `<span><b>Date</b> ${text(full.pour.date)}</span>`,
    `<span><b>Status</b> ${text(full.pour.status)}</span>`,
    `<span><b>Recorded by</b> ${text(full.pour.created_by)}</span>`,
    // Job defaults — facts about the job the record was made under.
    project.product ? `<span><b>Product</b> ${text(project.product)}</span>` : '',
    project.sci_unit_no ? `<span><b>SCI unit no</b> ${text(project.sci_unit_no)}</span>` : '',
    project.acceptance_point
      ? `<span><b>Acceptance point</b> ${text(project.acceptance_point)}</span>`
      : '',
    full.pour.closed_at
      ? `<span><b>Closed</b> ${text(full.pour.closed_at)} by ${text(full.pour.closed_by)}</span>`
      : '',
    `<span><b>Exported</b> ${text(exportedAt)}${input.exported_by ? ` by ${text(input.exported_by)}` : ''}</span>`,
    '</div>',
    '</div>'
  );

  if (blocking.length > 0) {
    const list = blocking.map((item) => `${item.item_no} ${item.title}`).join('; ');
    parts.push(
      `<p class="alarm">OPEN FAILED HOLD POINT${blocking.length === 1 ? '' : 'S'}: ${escapeHtml(list)}. Production is to stop and Kiewit QC to be notified before restart.</p>`
    );
  }

  parts.push(
    '<h2>Header</h2>',
    fieldTable([
      ['Pour location', full.header.pour_location],
      ['SCI unit no', full.header.sci_unit_no],
      ['Operator', full.header.operator],
      ['Feed / water control operator', full.header.feed_water_control_operator],
      ['Pump operator', full.header.pump_operator],
      ['Kiewit QC tester', full.header.kiewit_qc_tester],
      ['Water source', full.header.water_source],
      ['Material lot no', full.header.material_lot_no]
    ])
  );

  parts.push(
    '<h2>Material and loading</h2>',
    fieldTable([
      ['Product name', full.material_loading.product_name],
      ['Lot / batch no', full.material_loading.lot_batch_no],
      ['Supersack numbers', full.material_loading.supersack_numbers],
      ['Quantity loaded (kg)', full.material_loading.quantity_loaded],
      ['Packaging condition', full.material_loading.packaging_condition],
      ['Storage condition', full.material_loading.storage_condition],
      ['Loaded by', full.material_loading.loaded_by]
    ])
  );

  parts.push(
    '<h2>Pre-pour calibration</h2>',
    fieldTable([
      ['Approved water dosage', cal.approved_water_dosage],
      ['Dry feed setting', cal.dry_feed_setting],
      ['Water flow meter setting', cal.water_flow_meter_setting],
      ['Approved production rate (m3/h)', cal.approved_production_rate],
      ['Material feed stable', yesNo(cal.material_feed_stable)],
      ['Water input stable', yesNo(cal.water_input_stable)],
      ['Temp ambient / water / grout (C)', `${cal.temp_ambient ?? '-'} / ${cal.temp_water ?? '-'} / ${cal.temp_grout ?? '-'}`],
      ['Pump / line readiness', cal.pump_line_readiness],
      ['Acceptance point', cal.acceptance_point],
      ['Stop-hold contacts', cal.stop_hold_contacts]
    ])
  );

  parts.push(
    '<h2>Calibration worksheet</h2>',
    fieldTable(calibrationWorksheetFields(cal)),
    '<h3>Trials</h3>',
    calibrationTrialsTable(full.calibration_trials),
    '<h3>Checks</h3>',
    calibrationChecksTable(full.calibration_checks)
  );

  parts.push(
    `<h2>Chute tests (${full.chute_tests.length})</h2>`,
    chuteTestsTable(full.chute_tests)
  );

  parts.push(
    `<h2>Stop-hold record (${full.stop_hold_events.length})</h2>`,
    stopHoldTable(full.stop_hold_events)
  );

  parts.push(
    '<h2>ITP items 6&ndash;31</h2>',
    logTable(
      [
        { label: 'No', num: true },
        { label: 'Item' },
        { label: 'Type' },
        { label: 'Status' },
        { label: 'SCI initials' },
        { label: 'Time' }
      ],
      full.itp_items.map((item) => ({
        note: item.notes,
        cells: [
          { value: item.item_no, num: true },
          { value: item.title },
          { value: item.point_type },
          { value: item.status.toUpperCase() },
          { value: item.initials },
          { value: item.recorded_at }
        ]
      })),
      'No ITP items recorded.'
    )
  );

  parts.push(
    '<h2>End-of-pour checklist</h2>',
    logTable(
      [
        { label: 'No', num: true },
        { label: 'Item' },
        { label: 'Status' },
        { label: 'SCI initials' },
        { label: 'Time' }
      ],
      full.end_of_pour_items.map((item) => ({
        note: item.notes,
        cells: [
          { value: item.item_no, num: true },
          { value: item.title },
          { value: item.status.toUpperCase() },
          { value: item.initials },
          { value: item.recorded_at }
        ]
      })),
      'No end-of-pour items recorded.'
    )
  );

  parts.push(
    `<h2>Photographs (${full.photos.length})</h2>`,
    // The pictures themselves, and nothing above them. A seven-column manifest
    // used to sit here; the owner, reading a real export: "can delete this
    // section as we have the photos below." Every column it carried is on the
    // plate — see `photoPlates`. Served from the same authenticated route the
    // app uses, so a printed record carries no second copy of the image data
    // and the browser fetches each one exactly once.
    full.photos.length === 0
      ? '<p class="empty">No photographs attached.</p>'
      : photoPlates(full.photos, full.pour.closed_at)
  );

  parts.push(
    '<p class="foot">',
    'This record carries no signature block. Every entry above is stamped with the name of the SCI person on shift and a server timestamp, ',
    'and every change is held in an append-only audit log. Produced by SCI for its own process control; Kiewit performs its own certified testing.',
    '</p>',
    '</div>'
  );

  return parts.filter((part) => part !== '').join('\n');
}

/**
 * The photographs, printed rather than merely listed.
 *
 * There is no manifest table above these any more: the plate carries every
 * column it had — the id and caption, the section and row, the timestamp, the
 * uploader, and the after-close marking — and a record that lists a photograph
 * and does not show it is not the record the crew hands a client anyway.
 *
 * Each image is wrapped in a link to its own full-size original, so a reader on
 * screen can click through. Print is unaffected: an anchor round an `<img>`
 * draws nothing.
 *
 * `loading="eager"` and `decoding="sync"`: the print path has one chance at
 * each image, and a lazily-loaded plate below the fold prints as a blank box.
 * `printHtmlDocument` additionally waits for them before opening the dialog.
 */
function photoPlates(photos: PourFull['photos'], closedAt: string | null): string {
  if (photos.length === 0) return '';
  const plates = photos
    .map((photo) => {
      const late = photoAddedAfterClose(photo, closedAt)
        ? '<span class="late">Added after close</span>'
        : '';
      const where = `${escapeHtml(photo.section ?? 'general')}${photo.row_id ? ` #${escapeHtml(photo.row_id)}` : ''}`;
      const href = apiPath.photoContent(photo.id);
      return (
        '<figure class="plate">' +
        `<a href="${href}" target="_blank" rel="noopener">` +
        `<img src="${href}" alt="${escapeHtml(photo.caption ?? `Photograph ${photo.id}`)}"` +
        ' loading="eager" decoding="sync">' +
        '</a>' +
        '<figcaption>' +
        late +
        `#${photo.id} ${text(photo.caption)}` +
        `<span class="who">${where} &middot; taken ${text(photo.taken_at)} &middot; ${text(photo.uploaded_by)}</span>` +
        '</figcaption>' +
        '</figure>'
      );
    })
    .join('');
  return `<div class="plates">${plates}</div>`;
}

/**
 * Title used for the browser tab and, in every current browser, as the default
 * Save-as-PDF filename.
 */
export function pourPrintTitle(input: PourPrintInput): string {
  return `${input.project.code} ${input.pour.pour.label} ${input.pour.pour.date}`;
}

/**
 * The complete, self-contained print document.
 *
 * No external stylesheet, font or image: it is one string that can be written
 * into an iframe, saved to disk or served from the Worker unchanged.
 */
export function buildPourPrintHtml(input: PourPrintInput): string {
  const title = pourPrintTitle(input);
  return [
    '<!doctype html>',
    '<html lang="en">',
    '<head>',
    '<meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<title>${escapeHtml(title)}</title>`,
    `<style>${POUR_PRINT_STYLESHEET}</style>`,
    '</head>',
    '<body>',
    buildPourPrintBody(input),
    '</body>',
    '</html>',
    ''
  ].join('\n');
}

/* ==========================================================================
 * Browser: hand the document to the print dialog
 * ======================================================================== */

/**
 * Minimal structural types for the handful of DOM members this uses.
 *
 * `src/lib/**` is compiled by BOTH tsconfigs, and the Worker one deliberately
 * has no DOM lib — a bare reference to `document` here would break
 * `npm run typecheck`. Declaring exactly what is touched keeps this module
 * importable from the Worker (which only ever calls the pure builders above)
 * while staying honest about what the browser path needs.
 */
interface PrintImage {
  /** False until the image has finished loading (or failed to). */
  complete: boolean;
  addEventListener(type: 'load' | 'error', listener: () => void): void;
}

interface PrintView {
  focus(): void;
  print(): void;
  addEventListener(type: 'afterprint', listener: () => void): void;
  /**
   * Enough of the frame's document to tell an empty one from ours, and to see
   * whether its photographs have arrived.
   */
  document?: {
    body?: { firstElementChild?: unknown } | null;
    images?: ArrayLike<PrintImage>;
  } | null;
}

interface PrintFrame {
  srcdoc: string;
  title: string;
  style: { cssText: string };
  readonly contentWindow: PrintView | null;
  setAttribute(name: string, value: string): void;
  addEventListener(type: 'load', listener: () => void): void;
  remove(): void;
}

interface PrintHost {
  document?: {
    body: { appendChild(node: PrintFrame): void } | null;
    createElement(tag: 'iframe'): PrintFrame;
  };
  setTimeout(handler: () => void, ms: number): unknown;
}

export interface PrintOptions {
  /** Milliseconds to keep the frame alive after printing before removing it. */
  cleanupDelayMs?: number;
  /**
   * How long to wait for the record's photographs before printing without
   * them. Long enough for site WiFi, short enough that a stalled image does
   * not mean no print at all.
   */
  imageTimeoutMs?: number;
  /** Called once the print dialog has been opened (or has failed to open). */
  onDone?: (error?: unknown) => void;
}

/**
 * Print a self-contained HTML document.
 *
 * A hidden same-document iframe rather than `window.open`:
 *   * popup blockers do not stop it,
 *   * the app page is never navigated away from, so a part-filled pour behind
 *     the dialog is untouched,
 *   * and it leaves no second window for the user to get lost in — the spec
 *     forbids anything that can trap someone mid-pour.
 *
 * Returns false when there is no DOM (the Worker, or a unit test).
 */
export function printHtmlDocument(html: string, options: PrintOptions = {}): boolean {
  const host = globalThis as unknown as PrintHost;
  const doc = host.document;
  const body = doc?.body;
  if (!doc || !body) return false;

  const frame = doc.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('tabindex', '-1');
  frame.title = 'Print preview';
  frame.style.cssText =
    'position:fixed;right:0;bottom:0;width:1px;height:1px;opacity:0;border:0;pointer-events:none;';

  let finished = false;
  const finish = (error?: unknown): void => {
    if (finished) return;
    finished = true;
    host.setTimeout(() => frame.remove(), options.cleanupDelayMs ?? 1000);
    options.onDone?.(error);
  };

  frame.addEventListener('load', () => {
    try {
      const view = frame.contentWindow;
      if (!view) throw new Error('The print view could not be created.');

      // Only print once the frame is actually showing OUR document.
      //
      // Inserting an iframe fires a `load` for its initial empty document, and
      // that event can arrive before the srcdoc navigation has replaced it.
      // Printing on it produced a blank sheet, which is why this used to need
      // a cancel and a second go before the data appeared. Assigning srcdoc
      // before insertion (below) makes that race rare; this check makes acting
      // on the empty document impossible, rather than merely unlikely.
      const printed = view.document?.body?.firstElementChild;
      if (!printed) return;

      const open = (): void => {
        // Chrome fires afterprint immediately for "Save as PDF"; Safari never
        // fires it at all. `finish` is therefore also armed on a timeout, and
        // is idempotent so whichever arrives first wins.
        view.addEventListener('afterprint', () => finish());
        view.focus();
        view.print();
        host.setTimeout(() => finish(), 500);
      };

      // Wait for the photographs. An image that has not decoded when the
      // dialog opens prints as a blank rectangle, and the client keeps the
      // blank — there is no second chance on paper. The wait is bounded: a
      // stalled image must cost a missing plate, never the whole print.
      const images = view.document?.images;
      const pending: PrintImage[] = [];
      for (let i = 0; i < (images?.length ?? 0); i += 1) {
        const image = images![i];
        if (!image.complete) pending.push(image);
      }
      if (pending.length === 0) {
        open();
        return;
      }
      let outstanding = pending.length;
      let opened = false;
      const openOnce = (): void => {
        if (opened) return;
        opened = true;
        open();
      };
      for (const image of pending) {
        const settled = (): void => {
          outstanding -= 1;
          if (outstanding === 0) openOnce();
        };
        image.addEventListener('load', settled);
        image.addEventListener('error', settled);
      }
      host.setTimeout(openOnce, options.imageTimeoutMs ?? 4000);
    } catch (error) {
      finish(error);
    }
  });

  // `srcdoc` keeps the document same-origin and avoids document.write entirely.
  // Set BEFORE insertion so the frame's first and only navigation is to this
  // content — see the guard above for why that ordering matters.
  frame.srcdoc = html;
  body.appendChild(frame);
  return true;
}

/** Build the pour record and open the print / Save-as-PDF dialog. */
export function printPourRecord(input: PourPrintInput, options: PrintOptions = {}): boolean {
  return printHtmlDocument(buildPourPrintHtml(input), options);
}
